<script setup lang="ts">
import { computed } from 'vue'
import {
  isMissingValue,
  isRecord,
  type ClinicCrfField,
  type ClinicCrfFieldPresentation,
  type ClinicCrfMissingCode,
  type MissingValue,
  type PartialDateValue,
} from '@clinic-crf/contract'
import { isoToInputDateTime, inputDateTimeToIso } from '../helpers'

const props = defineProps<{
  field: ClinicCrfField
  value?: unknown
  required: boolean
  errors: string[]
  presentation?: ClinicCrfFieldPresentation
  showErrors?: boolean
}>()

const emit = defineEmits<{
  update: [value: unknown | undefined]
  touched: []
}>()

const missingLabels: Record<ClinicCrfMissingCode, string> = {
  unknown: 'Nieznane',
  not_done: 'Nie wykonano',
  not_applicable: 'Nie dotyczy',
  withheld: 'Odmowa / zastrzeżone',
  other: 'Inny powód',
}

const sourceLabels: Record<string, string> = {
  site: 'ośrodek',
  subject: 'uczestnik',
  device: 'urządzenie',
  ehr: 'EHR',
  derived: 'wyliczane',
}

const months = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień',
]

const inputId = computed(() => `field-${props.field.path.replace(/[^a-zA-Z0-9_-]/g, '-')}`)
const errorId = computed(() => `${inputId.value}-errors`)
const helpId = computed(() => `${inputId.value}-help`)
const missing = computed<MissingValue | null>(() => isMissingValue(props.value) ? props.value : null)
const normalValue = computed(() => missing.value ? undefined : props.value)
const stringValue = computed(() => typeof normalValue.value === 'string' ? normalValue.value : '')
const numberValue = computed(() => typeof normalValue.value === 'number' ? normalValue.value : '')
const booleanValue = computed(() => typeof normalValue.value === 'boolean' ? normalValue.value : undefined)
const multiValue = computed<string[]>(() => Array.isArray(normalValue.value) ? normalValue.value.filter((item): item is string => typeof item === 'string') : [])
const partialValue = computed<PartialDateValue | null>(() => {
  if (!isRecord(normalValue.value) || !Number.isInteger(normalValue.value.year)) return null
  return normalValue.value as unknown as PartialDateValue
})
const describedBy = computed(() => [props.presentation?.help_text ? helpId.value : '', props.showErrors && props.errors.length ? errorId.value : ''].filter(Boolean).join(' ') || undefined)
const step = computed(() => props.field.type === 'integer' ? '1' : props.field.precision === undefined ? 'any' : String(1 / (10 ** props.field.precision)))
const characterCount = computed(() => ['string', 'text'].includes(props.field.type) ? stringValue.value.length : null)

function targetValue(event: Event): string {
  return (event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value
}

function targetChecked(event: Event): boolean {
  return (event.target as HTMLInputElement).checked
}

function updateString(event: Event): void {
  const value = targetValue(event)
  emit('update', value === '' ? undefined : value)
}

function updateNumber(event: Event): void {
  const value = targetValue(event)
  emit('update', value === '' ? undefined : Number(value))
}

function updateDateTime(event: Event): void {
  emit('update', inputDateTimeToIso(targetValue(event)))
}

function updateBoolean(value: boolean): void {
  emit('update', value)
  emit('touched')
}

function updateMulti(option: string, checked: boolean): void {
  const next = new Set(multiValue.value)
  if (checked) next.add(option)
  else next.delete(option)
  emit('update', next.size ? Array.from(next) : undefined)
  emit('touched')
}

function updatePartial(key: keyof PartialDateValue, rawValue: string): void {
  if (key === 'year') {
    if (rawValue === '') {
      emit('update', undefined)
      return
    }
    emit('update', { ...(partialValue.value ?? {}), year: Number(rawValue) } satisfies PartialDateValue)
    return
  }
  if (!partialValue.value) return
  const next: PartialDateValue = { ...partialValue.value }
  if (key === 'month') {
    if (rawValue === '') {
      delete next.month
      delete next.day
    } else next.month = Number(rawValue)
  } else if (rawValue === '') delete next.day
  else next.day = Number(rawValue)
  emit('update', next)
}

function toggleMissing(event: Event): void {
  const checked = (event.target as HTMLInputElement).checked
  if (!checked) {
    emit('update', undefined)
  } else {
    const code = props.field.missing_codes?.[0]
    if (code) emit('update', { __missing: { code } } satisfies MissingValue)
  }
  emit('touched')
}

function updateMissingCode(event: Event): void {
  const code = targetValue(event) as ClinicCrfMissingCode
  const reason = missing.value?.__missing.reason
  emit('update', { __missing: { code, ...(reason ? { reason } : {}) } } satisfies MissingValue)
  emit('touched')
}

function updateMissingReason(event: Event): void {
  const code = missing.value?.__missing.code ?? props.field.missing_codes?.[0]
  if (!code) return
  const reason = targetValue(event)
  emit('update', { __missing: { code, ...(reason ? { reason } : {}) } } satisfies MissingValue)
}

function optionLabel(option: string): string {
  return props.presentation?.option_labels?.[option] ?? option
}
</script>

<template>
  <article
    class="field-card"
    :class="{ 'field-card--error': showErrors && errors.length, 'field-card--half': presentation?.width === 'half' }"
    :data-field-path="field.path"
  >
    <header class="field-card__header">
      <div>
        <label v-if="!['boolean', 'multichoice'].includes(field.type)" class="field-label" :for="inputId">
          {{ field.label }}
          <span v-if="required" class="required-mark" aria-label="wymagane">*</span>
        </label>
        <div v-else class="field-label" :id="`${inputId}-legend`">
          {{ field.label }}
          <span v-if="required" class="required-mark" aria-label="wymagane">*</span>
        </div>
        <p v-if="presentation?.help_text" :id="helpId" class="field-help">{{ presentation.help_text }}</p>
      </div>
      <span v-if="field.unit" class="unit-pill">{{ field.unit }}</span>
    </header>

    <div class="field-meta" aria-label="Adnotacje pola">
      <code>{{ field.path }}</code>
      <span>{{ field.type }}</span>
      <span v-if="field.source">źródło: {{ sourceLabels[field.source] ?? field.source }}</span>
      <span v-if="field.min !== undefined || field.max !== undefined">zakres: {{ field.min ?? '−∞' }}–{{ field.max ?? '+∞' }}</span>
      <span v-if="field.precision !== undefined">precyzja: {{ field.precision }}</span>
    </div>

    <div v-if="field.source === 'derived'" class="derived-notice" role="note">
      Pole wyliczane. Reader nie symuluje algorytmu źródłowego i nie pozwala wpisywać wartości ręcznie.
    </div>

    <div v-else-if="!missing" class="field-control">
      <input
        v-if="field.type === 'string'"
        :id="inputId"
        class="control"
        type="text"
        :value="stringValue"
        :placeholder="presentation?.placeholder"
        :minlength="field.min_length"
        :maxlength="field.max_length ?? 4000"
        :required="required"
        :aria-describedby="describedBy"
        :aria-invalid="showErrors && errors.length ? 'true' : undefined"
        autocomplete="off"
        @input="updateString"
        @blur="emit('touched')"
      />

      <div v-else-if="field.type === 'text'">
        <textarea
          :id="inputId"
          class="control control--textarea"
          :value="stringValue"
          :placeholder="presentation?.placeholder"
          :minlength="field.min_length"
          :maxlength="field.max_length ?? 100000"
          :required="required"
          :aria-describedby="describedBy"
          :aria-invalid="showErrors && errors.length ? 'true' : undefined"
          rows="5"
          @input="updateString"
          @blur="emit('touched')"
        />
        <span class="character-count">{{ characterCount }} / {{ field.max_length ?? 100000 }}</span>
      </div>

      <div v-else-if="field.type === 'integer' || field.type === 'number'" class="control-with-suffix">
        <input
          :id="inputId"
          class="control"
          type="number"
          inputmode="decimal"
          :value="numberValue"
          :min="field.min"
          :max="field.max"
          :step="step"
          :required="required"
          :aria-describedby="describedBy"
          :aria-invalid="showErrors && errors.length ? 'true' : undefined"
          @input="updateNumber"
          @blur="emit('touched')"
        />
        <span v-if="field.unit" aria-hidden="true">{{ field.unit }}</span>
      </div>

      <fieldset v-else-if="field.type === 'boolean'" class="choice-grid" :aria-describedby="describedBy" :aria-invalid="showErrors && errors.length ? 'true' : undefined">
        <legend class="sr-only">{{ field.label }}</legend>
        <label class="choice-tile" :class="{ 'choice-tile--selected': booleanValue === true }">
          <input :name="inputId" type="radio" :checked="booleanValue === true" @change="updateBoolean(true)" />
          <span>Tak</span>
        </label>
        <label class="choice-tile" :class="{ 'choice-tile--selected': booleanValue === false }">
          <input :name="inputId" type="radio" :checked="booleanValue === false" @change="updateBoolean(false)" />
          <span>Nie</span>
        </label>
        <button v-if="booleanValue !== undefined" class="button button--text choice-clear" type="button" @click="emit('update', undefined)">Wyczyść</button>
      </fieldset>

      <input
        v-else-if="field.type === 'date'"
        :id="inputId"
        class="control control--compact"
        type="date"
        :value="stringValue"
        :required="required"
        :aria-describedby="describedBy"
        :aria-invalid="showErrors && errors.length ? 'true' : undefined"
        @input="updateString"
        @blur="emit('touched')"
      />

      <div v-else-if="field.type === 'datetime'" class="datetime-control">
        <input
          :id="inputId"
          class="control control--compact"
          type="datetime-local"
          :value="isoToInputDateTime(normalValue)"
          :required="required"
          :aria-describedby="describedBy"
          :aria-invalid="showErrors && errors.length ? 'true' : undefined"
          @input="updateDateTime"
          @blur="emit('touched')"
        />
        <p class="microcopy">Czas jest zapisywany wyłącznie w pamięci jako ISO 8601 ze strefą.</p>
      </div>

      <select
        v-else-if="field.type === 'choice'"
        :id="inputId"
        class="control control--select"
        :value="stringValue"
        :required="required"
        :aria-describedby="describedBy"
        :aria-invalid="showErrors && errors.length ? 'true' : undefined"
        @change="updateString($event); emit('touched')"
      >
        <option value="">— Wybierz odpowiedź —</option>
        <option v-for="option in field.options" :key="option" :value="option">{{ optionLabel(option) }}</option>
      </select>

      <fieldset v-else-if="field.type === 'multichoice'" class="checkbox-list" :aria-describedby="describedBy" :aria-invalid="showErrors && errors.length ? 'true' : undefined">
        <legend class="sr-only">{{ field.label }}</legend>
        <label v-for="option in field.options" :key="option" class="checkbox-row">
          <input
            type="checkbox"
            :checked="multiValue.includes(option)"
            @change="updateMulti(option, targetChecked($event))"
          />
          <span>{{ optionLabel(option) }}</span>
          <code>{{ option }}</code>
        </label>
      </fieldset>

      <fieldset v-else-if="field.type === 'partial_date'" class="partial-date" :aria-describedby="describedBy" :aria-invalid="showErrors && errors.length ? 'true' : undefined">
        <legend class="sr-only">{{ field.label }}</legend>
        <label>
          <span>Rok</span>
          <input
            :id="inputId"
            class="control"
            type="number"
            inputmode="numeric"
            min="1000"
            max="9999"
            step="1"
            :required="required"
            :value="partialValue?.year ?? ''"
            placeholder="RRRR"
            :aria-describedby="describedBy"
            :aria-invalid="showErrors && errors.length ? 'true' : undefined"
            @input="updatePartial('year', targetValue($event))"
            @blur="emit('touched')"
          />
        </label>
        <label>
          <span>Miesiąc <small>(opcjonalnie)</small></span>
          <select
            class="control"
            :value="partialValue?.month ?? ''"
            :disabled="!partialValue?.year"
            @change="updatePartial('month', targetValue($event)); emit('touched')"
          >
            <option value="">—</option>
            <option v-for="(month, index) in months" :key="month" :value="index + 1">{{ index + 1 }} — {{ month }}</option>
          </select>
        </label>
        <label>
          <span>Dzień <small>(opcjonalnie)</small></span>
          <select
            class="control"
            :value="partialValue?.day ?? ''"
            :disabled="!partialValue?.month"
            @change="updatePartial('day', targetValue($event)); emit('touched')"
          >
            <option value="">—</option>
            <option v-for="day in 31" :key="day" :value="day">{{ day }}</option>
          </select>
        </label>
      </fieldset>
    </div>

    <section v-else class="missing-box" aria-label="Kontrolowany brak danych">
      <div class="missing-box__heading">
        <strong>Kontrolowany brak danych</strong>
        <span>Wartość pola została zastąpiona kodem.</span>
      </div>
      <label>
        <span>Kod braku</span>
        <select :id="inputId" class="control" :value="missing.__missing.code" @change="updateMissingCode">
          <option v-for="code in field.missing_codes" :key="code" :value="code">{{ missingLabels[code] }} — {{ code }}</option>
        </select>
      </label>
      <label>
        <span>Powód <b v-if="field.missing_reason_required">— wymagany</b></span>
        <textarea
          class="control control--textarea control--textarea-short"
          :value="missing.__missing.reason ?? ''"
          maxlength="1000"
          placeholder="Udokumentuj przyczynę użycia kodu braku danych"
          @input="updateMissingReason"
          @blur="emit('touched')"
        />
      </label>
    </section>

    <label v-if="field.source !== 'derived' && field.missing_codes?.length" class="missing-toggle">
      <input type="checkbox" :checked="Boolean(missing)" @change="toggleMissing" />
      <span>Oznacz jako brak danych</span>
    </label>

    <ul v-if="showErrors && errors.length" :id="errorId" class="field-errors" aria-live="polite">
      <li v-for="error in errors" :key="error">{{ error }}</li>
    </ul>
  </article>
</template>
